import {useTranslation} from "react-i18next";
import { RouteName, RouteParams } from 'ziggy-js';
import {usePage} from "@inertiajs/react";

type LocalizedRoute = {
    localizedRouteName(name: RouteName, locale?: string): RouteName,
    routeBaseName: (name: RouteName) => RouteName,
    localizedRoute: (name: RouteName, params?: RouteParams<string & {}>, locale?: string) => string,
};

export function useLocalizedRoute(): LocalizedRoute {
    const {i18n} = useTranslation();
    const {supportedLocales} = usePage().props.config;

    /**
     * Add locale prefix to route name:
     *
     * localizedRouteName('home') => 'de.home' or 'en.home'
     */
    function localizedRouteName(name: RouteName, locale?: string): RouteName {
        if (!locale)
            locale = i18n.language;

        return `${locale}.${name}` as RouteName;
    }

    /**
     * Remove locale prefix from route name
     *
     * routeBaseName('de.home') => 'home'
     */
    function routeBaseName(name: RouteName): RouteName {
        const segments = name.split('.');

        // Remove first segment only if it's a locale
        if (segments.length > 1 && Object.values(supportedLocales).includes(segments[0]))
            segments.shift();

        return segments.join('.');
    }

    /**
     * Generate localized route
     *
     * localizedRoute('home') => '/de/home' or '/en/home'
     */
    function localizedRoute(name: RouteName, params?: RouteParams<string & {}>, locale?: string): string {
        return route(localizedRouteName(name, locale), params);
    }

    return <LocalizedRoute>{
        localizedRouteName,
        routeBaseName,
        localizedRoute,
    };
}
